import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { StatusBadge } from '../StatusBadge';
import { MoreHorizontal, ExternalLink, Trash2, Download, RotateCcw, Video, Clock } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

export const ProjectCard = ({ project, onDelete, onRetry }) => {
  const navigate = useNavigate();

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const modeLabel = {
    idea: 'Idea',
    script: 'Script',
    upload: 'Upload'
  };

  const isActive = ['script_generating', 'audio_generating', 'video_rendering'].includes(project.status);

  return (
    <Card
      className="project-card cursor-pointer rounded-xl border bg-card overflow-hidden shadow-[0_1px_0_rgba(15,23,42,0.04),0_14px_30px_rgba(15,23,42,0.06)]"
      data-testid="project-card"
      onClick={() => navigate(`/projects/${project._id}`)}
    >
      {/* Thumbnail / Cover area */}
      <div className="relative h-36 bg-gradient-to-br from-[hsl(40,20%,97%)] to-[hsl(40,25%,94%)] flex items-center justify-center">
        {project.status === 'completed' && project.thumbnail_url ? (
          <img
            src={project.thumbnail_url}
            alt={project.name}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="flex flex-col items-center gap-2">
            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${isActive ? 'bg-[hsl(38,78%,56%)]/15' : 'bg-white/80'}`}>
              <Video className={`w-6 h-6 ${isActive ? 'text-[hsl(38,78%,45%)] animate-pulse-dot' : 'text-muted-foreground'}`} />
            </div>
          </div>
        )}

        {/* Status badge overlay */}
        <div className="absolute top-3 left-3">
          <StatusBadge status={project.status} />
        </div>

        {/* Engine badge overlay */}
        {project.engine_used && (
          <div className="absolute top-3 right-3">
            <Badge
              className="bg-black/60 backdrop-blur-sm text-white border-0 text-[10px] px-1.5 py-0.5 font-mono font-bold uppercase tracking-wider"
              data-testid="project-card-engine-badge"
            >
              {project.engine_used === 'avatar_iv' ? 'v4' : project.engine_used === 'avatar_v3_fallback' ? 'v3' : project.engine_used}
            </Badge>
          </div>
        )}

        {/* Actions menu */}
        <div className="absolute top-3 right-3" style={{ right: project.engine_used ? '52px' : '12px' }}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="icon" className="h-7 w-7 rounded-lg bg-white/80 backdrop-blur-sm hover:bg-white" data-testid="project-card-actions-menu">
                <MoreHorizontal className="w-3.5 h-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/projects/${project._id}`); }}>
                <ExternalLink className="w-4 h-4 mr-2" /> Open
              </DropdownMenuItem>
              {project.status === 'failed' && (
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onRetry?.(project._id); }}>
                  <RotateCcw className="w-4 h-4 mr-2" /> Retry
                </DropdownMenuItem>
              )}
              {project.status === 'completed' && project.local_video_url && (
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); window.open(project.local_video_url, '_blank'); }}>
                  <Download className="w-4 h-4 mr-2" /> Download
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                className="text-red-600"
                onClick={(e) => { e.stopPropagation(); onDelete?.(project._id); }}
              >
                <Trash2 className="w-4 h-4 mr-2" /> Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Card content */}
      <div className="p-4">
        <h3 className="font-heading font-semibold text-base truncate mb-1" data-testid="project-card-title">{project.name}</h3>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>{modeLabel[project.input_mode] || 'Idea'}</span>
          <span className="w-1 h-1 rounded-full bg-muted-foreground/40" />
          <span>{project.duration_mode || '60'}s</span>
          <span className="w-1 h-1 rounded-full bg-muted-foreground/40" />
          <Clock className="w-3 h-3" />
          <span>{formatDate(project.created_at)}</span>
        </div>

        {project.error && (
          <p className="text-xs text-red-500 mt-2 truncate" data-testid="project-card-error">{project.error}</p>
        )}
      </div>
    </Card>
  );
};
